// Thin HTTP layer for the PrintAnywhere consumer API (same backend the mobile app uses).
// The API is fronted by the Spring Cloud gateway which already answers CORS for browsers.

// REACT_APP_API_URL="" (empty) makes requests relative so the CRA dev-server proxy can forward
// them to a local backend; unset means the production API.
const envBase = process.env.REACT_APP_API_URL;
export const API_BASE = (envBase === undefined ? "https://smitprint.printanywhere.in" : envBase).replace(/\/+$/, "");
export const USER_API = "/user/flutter";
// Sent as App-Version; the backend skips the store-version gate for Platform "web".
export const WEB_APP_VERSION = "web-1.0.0";

const KEYS = {
  token: "pa_token",
  mobile: "pa_mobile",
  merchant: "pa_merchant",
  profile: "pa_profile",
};

const AUTH_EVENT = "pa-auth-changed";

function read(key) {
  try {
    return localStorage.getItem(key);
  } catch (e) {
    return null;
  }
}

function write(key, value) {
  try {
    if (value === null || value === undefined) localStorage.removeItem(key);
    else localStorage.setItem(key, value);
  } catch (e) {
    /* storage unavailable (private mode) – session simply won't persist */
  }
}

function notify() {
  try {
    window.dispatchEvent(new Event(AUTH_EVENT));
  } catch (e) {
    /* ignore */
  }
}

export const auth = {
  getToken: () => read(KEYS.token),
  getMobile: () => read(KEYS.mobile),
  getMerchantId: () => read(KEYS.merchant),
  getProfile: () => {
    try {
      return JSON.parse(read(KEYS.profile) || "null");
    } catch (e) {
      return null;
    }
  },
  isLoggedIn: () => !!read(KEYS.token),
  setSession({ token, mobile, merchantId }) {
    write(KEYS.token, token);
    write(KEYS.mobile, mobile);
    write(KEYS.merchant, merchantId || "");
    notify();
  },
  setProfile(profile) {
    write(KEYS.profile, profile ? JSON.stringify(profile) : null);
    notify();
  },
  clear() {
    Object.values(KEYS).forEach((k) => write(k, null));
    notify();
  },
};

export function onAuthChange(listener) {
  window.addEventListener(AUTH_EVENT, listener);
  window.addEventListener("storage", listener);
  return () => {
    window.removeEventListener(AUTH_EVENT, listener);
    window.removeEventListener("storage", listener);
  };
}

export async function sha256Hex(text) {
  const bytes = new TextEncoder().encode(text);
  const digest = await crypto.subtle.digest("SHA-256", bytes);
  return Array.from(new Uint8Array(digest))
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
}

export class ApiError extends Error {
  constructor(message, status, body) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.body = body;
  }
}

export function extractMessage(body, fallback) {
  if (body === null || body === undefined || body === "") return fallback;
  if (typeof body === "string") return body;
  if (body.title && body.description) return `${body.title} ${body.description}`;
  if (typeof body.data === "string") return body.data;
  if (typeof body.message === "string") return body.message;
  return fallback;
}

function baseHeaders(withAuth) {
  const headers = { "App-Version": WEB_APP_VERSION, Platform: "web" };
  if (withAuth) {
    const token = auth.getToken();
    if (token) headers.Authorization = token;
  }
  return headers;
}

async function parseBody(response) {
  const text = await response.text();
  if (!text) return null;
  try {
    return JSON.parse(text);
  } catch (e) {
    return text;
  }
}

function handleStatus(status, data, withAuth) {
  if (status === 401 && withAuth && auth.getToken()) {
    auth.clear();
    throw new ApiError("Your session has expired. Please log in again.", 401, data);
  }
  if (status === 426) {
    throw new ApiError("PrintAnywhere is being updated. Please try again in a few minutes.", 426, data);
  }
  if (status === 430) {
    throw new ApiError(extractMessage(data, "Too many OTP requests. Please try again after 30 minutes."), 430, data);
  }
}

export async function request(path, { method = "GET", json, headers = {}, withAuth = true, signal } = {}) {
  const url = path.startsWith("http") ? path : API_BASE + path;
  const finalHeaders = { ...baseHeaders(withAuth), ...headers };
  let body;
  if (json !== undefined) {
    finalHeaders["Content-Type"] = "application/json";
    body = JSON.stringify(json);
  }
  let response;
  try {
    response = await fetch(url, { method, headers: finalHeaders, body, signal });
  } catch (e) {
    if (e && e.name === "AbortError") throw e;
    throw new ApiError("Unable to reach PrintAnywhere. Please check your internet connection.", 0, null);
  }
  const data = await parseBody(response);
  handleStatus(response.status, data, withAuth);
  if (!response.ok) {
    throw new ApiError(extractMessage(data, `Something went wrong (${response.status})`), response.status, data);
  }
  return data;
}

/**
 * Multipart upload with progress. Returns { promise, abort }.
 */
export function upload(path, { formData, onProgress, withAuth = true }) {
  const xhr = new XMLHttpRequest();
  const promise = new Promise((resolve, reject) => {
    xhr.open("POST", API_BASE + path);
    Object.entries(baseHeaders(withAuth)).forEach(([k, v]) => xhr.setRequestHeader(k, v));
    xhr.upload.onprogress = (event) => {
      if (event.lengthComputable && onProgress) onProgress(event.loaded, event.total);
    };
    xhr.onload = () => {
      let data;
      try {
        data = JSON.parse(xhr.responseText);
      } catch (e) {
        data = xhr.responseText;
      }
      try {
        handleStatus(xhr.status, data, withAuth);
      } catch (err) {
        reject(err);
        return;
      }
      if (xhr.status >= 200 && xhr.status < 300) resolve(data);
      else reject(new ApiError(extractMessage(data, `Upload failed (${xhr.status})`), xhr.status, data));
    };
    xhr.onerror = () => reject(new ApiError("Upload failed. Please check your internet connection.", 0, null));
    xhr.onabort = () => reject(new ApiError("Upload cancelled", 0, null));
    xhr.send(formData);
  });
  return { promise, abort: () => xhr.abort() };
}
